import { useTranslation } from 'react-i18next';
import { ChevronsDown, ChevronsUp, Inbox, RefreshCw } from 'lucide-react';
import { Button, Skeleton } from '@shared/ui';
import type { WalletMonitoringAddress } from '@shared/api/contracts';
import type { WalletGroupSection, WalletFilterCatalog, WalletsMonitoringFilters } from '@entities/wallets';
import { WalletGroupCard } from './wallet-group-card';

interface WalletGroupListProps {
  sections: WalletGroupSection[];
  filters: WalletsMonitoringFilters;
  catalog?: WalletFilterCatalog;
  totalWallets: number;
  expandedGroups: Set<string>;
  expandedWalletIds: Set<number>;
  isLoading?: boolean;
  error?: unknown;
  onRefresh?: () => void;
  onToggleGroup: (groupCode: string) => void;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  onToggleWallet: (walletId: number) => void;
  onOpenControlMode: (address: WalletMonitoringAddress) => void;
  onToggleAml: (address: WalletMonitoringAddress) => void;
  isAmlPending: (addressId: number) => boolean;
  isControlModePending: (addressId: number) => boolean;
}

export function WalletGroupList({
  sections,
  filters,
  catalog,
  totalWallets,
  expandedGroups,
  expandedWalletIds,
  isLoading,
  error,
  onRefresh,
  onToggleGroup,
  onExpandAllGroups,
  onCollapseAllGroups,
  onToggleWallet,
  onOpenControlMode,
  onToggleAml,
  isAmlPending,
  isControlModePending,
}: WalletGroupListProps) {
  const { t } = useTranslation('cabinet');

  return (
    <div className="space-y-2.5">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {t('wallets.resultsCount', { count: totalWallets })}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-tap text-xs sm:h-8"
            onClick={onExpandAllGroups}
            disabled={!sections.length}
          >
            <ChevronsDown className="mr-1.5 h-3.5 w-3.5" />
            {t('wallets.expandAll')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-tap text-xs sm:h-8"
            onClick={onCollapseAllGroups}
            disabled={!sections.length}
          >
            <ChevronsUp className="mr-1.5 h-3.5 w-3.5" />
            {t('wallets.collapseAll')}
          </Button>
          {onRefresh && (
            <Button
              variant="outline"
              size="icon"
              className="h-tap w-tap rounded-full sm:h-8 sm:w-8"
              onClick={onRefresh}
              loading={isLoading}
              leftIcon={<RefreshCw className="size-4" />}
              aria-label={t('common.retry', { ns: 'common' })}
            />
          )}
        </div>
      </div>

      {error ? (
        <div className="empty-state">
          <p className="section-description">{t('wallets.loadError')}</p>
          {onRefresh && (
            <Button variant="outline" onClick={onRefresh}>
              {t('common.retry', { ns: 'common' })}
            </Button>
          )}
        </div>
      ) : isLoading && !sections.length ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : !sections.length ? (
        <div className="empty-state">
          <Inbox className="h-8 w-8 text-muted-foreground sm:h-10 sm:w-10" />
          <p className="section-description">{t('wallets.empty')}</p>
        </div>
      ) : (
        sections.map((section) => (
          <WalletGroupCard
            key={section.group.code}
            section={section}
            filters={filters}
            catalog={catalog}
            expanded={expandedGroups.has(section.group.code)}
            expandedWalletIds={expandedWalletIds}
            onToggle={() => onToggleGroup(section.group.code)}
            onToggleWallet={onToggleWallet}
            onOpenControlMode={onOpenControlMode}
            onToggleAml={onToggleAml}
            isAmlPending={isAmlPending}
            isControlModePending={isControlModePending}
          />
        ))
      )}
    </div>
  );
}
